import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './CommandPalette.css';

const PAGES = [
  { id: 'dashboard', label: 'Dashboard', shortcut: '⌘1', path: '/', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  )},
  { id: 'backup', label: 'Backup', shortcut: '⌘2', path: '/backup', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
    </svg>
  )},
  { id: 'import', label: 'Import', shortcut: '⌘3', path: '/import', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  )},
  { id: 'media-upload', label: 'Media Upload', shortcut: '⌘4', path: '/media-upload', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  )},
  { id: 'tasks', label: 'Tasks', shortcut: '⌘5', path: '/tasks', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
    </svg>
  )},
  { id: 'wipe', label: 'Wipe Data', shortcut: '⌘6', path: '/wipe', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  )},
];

const ACTIONS = [
  { id: 'go-backup', label: 'Create Backup', path: '/backup', description: 'Go to backup page' },
  { id: 'go-import', label: 'Start Import', path: '/import', description: 'Go to import page' },
  { id: 'go-tasks', label: 'View Tasks', path: '/tasks', description: 'Monitor active tasks' },
];

function CommandPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const filteredPages = PAGES.filter(p =>
    p.label.toLowerCase().includes(query.toLowerCase())
  );
  const filteredActions = ACTIONS.filter(a =>
    a.label.toLowerCase().includes(query.toLowerCase()) ||
    a.description.toLowerCase().includes(query.toLowerCase())
  );

  const allItems = [...filteredPages, ...filteredActions];

  const handleSelect = useCallback((item) => {
    navigate(item.path);
    onClose();
  }, [navigate, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, allItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && allItems[selectedIndex]) {
        handleSelect(allItems[selectedIndex]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, allItems, selectedIndex, onClose, handleSelect]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  const pageOffset = 0;
  const actionOffset = filteredPages.length;

  return (
    <div className="command-overlay" onClick={onClose}>
      <div className="command-palette" onClick={e => e.stopPropagation()}>
        <div className="command-input-wrapper">
          <svg className="command-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            className="command-input"
            placeholder="Search pages and actions..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <kbd className="command-esc-hint">ESC</kbd>
        </div>

        <div className="command-results">
          {filteredPages.length > 0 && (
            <div className="command-group">
              <div className="command-group-label">Pages</div>
              {filteredPages.map((page, i) => (
                <div
                  key={page.id}
                  className={`command-item ${selectedIndex === pageOffset + i ? 'selected' : ''}`}
                  onClick={() => handleSelect(page)}
                  onMouseEnter={() => setSelectedIndex(pageOffset + i)}
                >
                  <span className="command-item-icon">{page.icon}</span>
                  <span className="command-item-label">{page.label}</span>
                  <kbd className="command-item-shortcut">{page.shortcut}</kbd>
                </div>
              ))}
            </div>
          )}

          {filteredActions.length > 0 && (
            <div className="command-group">
              <div className="command-group-label">Actions</div>
              {filteredActions.map((action, i) => (
                <div
                  key={action.id}
                  className={`command-item ${selectedIndex === actionOffset + i ? 'selected' : ''}`}
                  onClick={() => handleSelect(action)}
                  onMouseEnter={() => setSelectedIndex(actionOffset + i)}
                >
                  <span className="command-item-label">{action.label}</span>
                  <span className="command-item-description">{action.description}</span>
                </div>
              ))}
            </div>
          )}

          {allItems.length === 0 && (
            <div className="command-empty">No results for "{query}"</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
